import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../services/api';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Copy, Navigation, Phone, CheckCircle, Mail, AlertCircle, Package, MapPin, Zap, ChevronRight, XCircle } from 'lucide-react';
import { VisitDetailModal } from '../components/visits/VisitDetailModal';
import { QuickVisitModal } from '../components/visits/QuickVisitModal';
import { MaterialsSummary } from '../components/routes/MaterialsSummary';
import { Visit } from '../types';
import L from 'leaflet';

interface VisitBatchItem {
  id: string;
  pos_id: string;
  pos_name: string;
  street: string;
  street_number: string;
  postal_code: string;
  city: string;
  latitude: number;
  longitude: number;
  contact_person_name?: string;
  contact_person_phone?: string;
  contact_person_email?: string;
  store_type: string;
  sequence_order: number;
  total_score: number;
  followup_score: number;
  time_score: number;
  proximity_score: number;
  value_score: number;
  distance_from_previous_km: number;
  visited: boolean;
  visit_id?: string;
  visited_at?: string;
  needs_followup?: boolean;
  followup_priority?: string;
  followup_materials?: string[];
  followup_actions?: string[];
  last_visit_date?: string;
  last_visit_outcome?: string;
  last_visit_type?: 'first_time' | 'revisit';
}

interface VisitBatch {
  id: string;
  name: string;
  description?: string;
  status: string;
  total_pos_count: number;
  total_distance_km: number;
  starting_point: string;
  planned_date: string;
  items: VisitBatchItem[];
}

const Routes: React.FC = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'current' | 'archived'>('current');
  const [activeRoute, setActiveRoute] = useState<VisitBatch | null>(null);
  const [archivedRoutes, setArchivedRoutes] = useState<VisitBatch[]>([]);
  const [selectedRoute, setSelectedRoute] = useState<VisitBatch | null>(null);
  const [selectedPoS, setSelectedPoS] = useState<VisitBatchItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedVisit, setSelectedVisit] = useState<Visit | null>(null);
  const [showVisitDetailModal, setShowVisitDetailModal] = useState(false);
  const [showQuickVisitModal, setShowQuickVisitModal] = useState(false);
  const [quickVisitFollowup, setQuickVisitFollowup] = useState<any>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  useEffect(() => {
    if (viewMode === 'current') {
      fetchActiveRoute();
    } else {
      fetchArchivedRoutes();
    }
    // Always fetch count for tab badge
    fetchArchivedCount();
  }, [viewMode]);

  const [archivedCount, setArchivedCount] = useState(0);

  const fetchArchivedCount = async () => {
    try {
      // Fetch both completed and archived routes for the archive view
      const [archivedRes, completedRes] = await Promise.all([
        api.get('/visit-batches?status=archived'),
        api.get('/visit-batches?status=completed'),
      ]);
      setArchivedCount(archivedRes.data.length + completedRes.data.length);
    } catch (err) {
      console.error('Failed to fetch archived count:', err);
    }
  };

  const fetchActiveRoute = async () => {
    try {
      setLoading(true);
      const res = await api.get('/visit-batches/active');
      
      if (res.data) {
        const detailsRes = await api.get(`/visit-batches/${res.data.id}`);
        setActiveRoute(detailsRes.data);
        setSelectedRoute(detailsRes.data);
      } else {
        setActiveRoute(null);
        setSelectedRoute(null);
      }
    } catch (err) {
      console.error('Failed to fetch active route:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivedRoutes = async () => {
    try {
      setLoading(true);
      // Fetch both completed and archived routes
      const [archivedRes, completedRes] = await Promise.all([
        api.get('/visit-batches?status=archived'),
        api.get('/visit-batches?status=completed'),
      ]);
      
      // Combine and sort by planned_date descending
      const allArchived = [...archivedRes.data, ...completedRes.data].sort(
        (a, b) => new Date(b.planned_date).getTime() - new Date(a.planned_date).getTime()
      );
      
      setArchivedRoutes(allArchived);
      if (allArchived.length > 0) {
        const detailsRes = await api.get(`/visit-batches/${allArchived[0].id}`);
        setSelectedRoute(detailsRes.data);
      } else {
        setSelectedRoute(null);
      }
    } catch (err) {
      console.error('Failed to fetch archived routes:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateGoogleMapsURL = () => {
    if (!activeRoute || !activeRoute.items || activeRoute.items.length === 0) return '';
    
    // Filter out items without valid coordinates
    const validItems = activeRoute.items.filter(item => 
      item.latitude && item.longitude && 
      !isNaN(parseFloat(item.latitude as any)) && 
      !isNaN(parseFloat(item.longitude as any))
    );
    
    if (validItems.length === 0) return '';
    
    const startPoint = typeof activeRoute.starting_point === 'string' 
      ? JSON.parse(activeRoute.starting_point) 
      : activeRoute.starting_point;
    const origin = `${startPoint.latitude},${startPoint.longitude}`;
    
    // Google Maps supports up to 10 waypoints (9 waypoints + 1 destination)
    const items = validItems.slice(0, 10);
    const lastItem = items[items.length - 1];
    const destination = `${lastItem.latitude},${lastItem.longitude}`;
    
    // All except the last one are waypoints
    const waypoints = items.slice(0, -1).map(item => `${item.latitude},${item.longitude}`).join('|');
    
    return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}${waypoints ? `&waypoints=${waypoints}` : ''}&travelmode=driving`;
  };

  const copyAddress = (item: VisitBatchItem) => {
    const address = `${item.street} ${item.street_number}, ${item.postal_code} ${item.city}`;
    navigator.clipboard.writeText(address);
    toast.success('Address copied to clipboard!');
  };

  const handleUpdateStatus = async (status: string) => {
    if (!currentRoute) return;
    
    try {
      await api.patch(`/visit-batches/${currentRoute.id}/status`, { status });
      fetchActiveRoute();
    } catch (err) {
      console.error('Failed to update route status:', err);
      toast.error('Failed to update route status');
    }
  };

  const handleCancelRoute = async () => {
    if (!currentRoute) return;

    try {
      await api.patch(`/visit-batches/${currentRoute.id}/status`, {
        status: 'cancelled',
      });
      toast.success('Route cancelled. All PoS are available for new routes.');
      setShowCancelConfirm(false);
      setActiveRoute(null);
      setSelectedRoute(null);
      fetchActiveRoute();
    } catch (err) {
      console.error('Failed to cancel route:', err);
      toast.error('Failed to cancel route');
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
        <p className="text-gray-500">Loading route...</p>
      </div>
    );
  }

  const currentRoute = selectedRoute;

  if (!selectedRoute) {
    return (
      <div className="p-6 max-w-[1800px] mx-auto">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-gray-200">
          <button
            onClick={() => setViewMode('current')}
            className={`px-6 py-3 font-bold uppercase tracking-wider text-sm transition-all border-b-2 ${
              viewMode === 'current'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Current Route
          </button>
          <button
            onClick={() => setViewMode('archived')}
            className={`px-6 py-3 font-bold uppercase tracking-wider text-sm transition-all border-b-2 ${
              viewMode === 'archived'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Archived Routes ({archivedCount})
          </button>
        </div>

        <div className="p-8 text-center max-w-2xl mx-auto">
          <Navigation size={64} className="mx-auto text-gray-300 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {viewMode === 'current' ? 'No Active Route' : 'No Archived Routes'}
          </h2>
          <p className="text-gray-600 mb-6">
            {viewMode === 'current' 
              ? 'Create a route from the Plan Visits feature to get started!'
              : 'Completed or archived routes will appear here.'}
          </p>
          {viewMode === 'current' && (
            <button
              onClick={() => window.location.href = '/visits'}
              className="px-6 py-3 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700"
            >
              Go to Visits
            </button>
          )}
        </div>
      </div>
    );
  }

  // Filter out items with invalid coordinates
  const validItems = currentRoute.items.filter(item => 
    item.latitude && item.longitude && 
    !isNaN(parseFloat(item.latitude as any)) && !isNaN(parseFloat(item.longitude as any))
  );

  const startPoint = typeof currentRoute.starting_point === 'string' 
    ? JSON.parse(currentRoute.starting_point) 
    : currentRoute.starting_point;
  
  // Use first valid PoS as map center if startPoint is invalid
  const mapCenter: [number, number] = startPoint.latitude && startPoint.longitude
    ? [startPoint.latitude, startPoint.longitude]
    : validItems.length > 0
    ? [parseFloat(validItems[0].latitude as any), parseFloat(validItems[0].longitude as any)]
    : [52.5200, 13.4050]; // Fallback to Berlin center

  const visitedCount = currentRoute.items.filter(item => item.visited).length;
  const progressPercent = (visitedCount / currentRoute.items.length) * 100;

  return (
    <div className="p-6 max-w-[1800px] mx-auto">
      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        <button
          onClick={() => setViewMode('current')}
          className={`px-6 py-3 font-bold uppercase tracking-wider text-sm transition-all border-b-2 ${
            viewMode === 'current'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          Current Route
        </button>
        <button
          onClick={() => setViewMode('archived')}
          className={`px-6 py-3 font-bold uppercase tracking-wider text-sm transition-all border-b-2 ${
            viewMode === 'archived'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          Archived Routes ({archivedCount})
        </button>
      </div>

      {/* Archived Routes List (shown only in archived view) */}
      {viewMode === 'archived' && archivedRoutes.length > 0 && (
        <div className="mb-6 bg-white border-2 border-gray-200 rounded-xl p-4">
          <h3 className="font-bold mb-3">Select a route to view:</h3>
          <div className="space-y-2">
            {archivedRoutes.map(route => (
              <button
                key={route.id}
                onClick={async () => {
                  const res = await api.get(`/visit-batches/${route.id}`);
                  setSelectedRoute(res.data);
                }}
                className={`w-full text-left p-3 border rounded-lg hover:bg-gray-50 transition-colors ${
                  selectedRoute?.id === route.id ? 'bg-indigo-50 border-indigo-400' : 'border-gray-200'
                }`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <div className="font-bold">{route.name}</div>
                    <div className="text-sm text-gray-600">
                      {new Date(route.planned_date).toLocaleDateString()} • {route.total_pos_count} stops
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 uppercase">{route.status}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-black text-gray-900 mb-2">{currentRoute.name}</h1>
        <div className="flex items-center gap-6 text-sm text-gray-600">
          <span>
            <strong>{visitedCount}</strong> / <strong>{currentRoute.items.length}</strong> visits completed
          </span>
          <span>
            <strong>{parseFloat(currentRoute.total_distance_km as any)?.toFixed(1) || 0}</strong> km total distance
          </span>
          <span className={`px-3 py-1 rounded-full font-bold ${
            currentRoute.status === 'completed' ? 'bg-green-100 text-green-700' :
            currentRoute.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
            currentRoute.status === 'archived' ? 'bg-gray-100 text-gray-700' :
            'bg-blue-100 text-blue-700'
          }`}>
            {currentRoute.status.replace('_', ' ').toUpperCase()}
          </span>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-4 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-green-500 transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Warning for missing coordinates */}
      {validItems.length < currentRoute.items.length && (
        <div className="mb-6 p-4 bg-yellow-50 border-2 border-yellow-200 rounded-xl">
          <div className="flex items-start gap-3">
            <AlertCircle size={24} className="text-yellow-600 flex-shrink-0" />
            <div>
              <strong className="font-bold text-yellow-900">Missing Coordinates</strong>
              <p className="text-sm text-yellow-700 mt-1">
                {currentRoute.items.length - validItems.length} PoS in this route don't have geocoded coordinates and won't appear on the map.
                These PoS were likely created before geocoding was implemented.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-4 mb-6">
        <a
          href={generateGoogleMapsURL()}
          target="_blank"
          rel="noopener noreferrer"
          className="px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors shadow-lg active:scale-95"
        >
          <Navigation size={20} className="inline mr-2" />
          Open in Google Maps
        </a>
        
        {viewMode === 'current' && currentRoute.status === 'planned' && (
          <button 
            onClick={() => handleUpdateStatus('in_progress')}
            className="px-6 py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition-colors shadow-lg active:scale-95"
          >
            Start Route
          </button>
        )}
        
        {viewMode === 'current' && currentRoute.status === 'in_progress' && (
          <button 
            onClick={() => handleUpdateStatus('completed')}
            className="px-6 py-3 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700 transition-colors shadow-lg active:scale-95"
          >
            Complete Route
          </button>
        )}

        {viewMode === 'current' && (currentRoute.status === 'planned' || currentRoute.status === 'in_progress') && (
          <button
            onClick={() => setShowCancelConfirm(true)}
            className="px-6 py-3 bg-red-100 text-red-700 rounded-xl font-bold hover:bg-red-200 transition-colors active:scale-95"
          >
            <XCircle size={20} className="inline mr-2" />
            Cancel Route
          </button>
        )}
        
        <button 
          onClick={() => window.location.href = '/visits'}
          className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition-colors"
        >
          Back to Visits
        </button>
      </div>

      {/* Cancel Confirmation Dialog */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 1000 }}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle size={28} className="text-red-600" />
              </div>
              <h3 className="text-xl font-black text-gray-900">Cancel Route?</h3>
            </div>
            <p className="text-gray-600 mb-2">
              This will cancel <strong>{currentRoute.name}</strong> and return all {currentRoute.items.length} PoS to the backlog.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              The PoS will be available again for your next planned route.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="flex-1 py-3 bg-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-300 transition-colors"
              >
                Keep Route
              </button>
              <button
                onClick={handleCancelRoute}
                className="flex-1 py-3 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-colors shadow-lg shadow-red-200"
              >
                Cancel Route
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Materials Summary */}
      <MaterialsSummary items={currentRoute.items} />

      <div className="flex flex-col lg:grid lg:grid-cols-3 gap-6">
        {/* Map - 2/3 width on desktop, full width on mobile */}
        <div className="lg:col-span-2 h-[400px] lg:h-[600px] border-2 border-gray-200 rounded-2xl overflow-hidden shadow-lg">
          <MapContainer 
            center={mapCenter} 
            zoom={12} 
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer 
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            
            {/* PoS Markers with numbers */}
            {validItems.map((item, index) => {
              const lat = parseFloat(item.latitude as any);
              const lng = parseFloat(item.longitude as any);
              
              const numberIcon = L.divIcon({
                html: `<div style="background: ${item.visited ? '#10b981' : '#3b82f6'}; color: white; width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 14px; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">${item.sequence_order}</div>`,
                className: '',
                iconSize: [36, 36],
              });

              return (
                <Marker 
                  key={item.id} 
                  position={[lat, lng]}
                  icon={numberIcon}
                  eventHandlers={{
                    click: () => setSelectedPoS(item),
                  }}
                >
                  <Popup>
                    <div className="font-bold text-sm">{item.pos_name}</div>
                    <div className="text-xs text-gray-600">{item.street} {item.street_number}</div>
                    <div className="text-xs text-gray-500 mt-1">#{item.sequence_order} in route</div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        </div>

        {/* PoS Details Sidebar - 1/3 width on desktop, full width below map on mobile */}
        <div className="border-2 border-gray-200 rounded-2xl overflow-hidden h-[500px] lg:h-[600px] bg-white shadow-lg flex flex-col">
          {selectedPoS ? (
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-black text-xl text-gray-900">{selectedPoS.pos_name}</h3>
                  <p className="text-sm text-gray-500 capitalize">{selectedPoS.store_type}</p>
                </div>
                {selectedPoS.visited && (
                  <div className="flex items-center gap-1 text-green-600">
                    <CheckCircle size={24} />
                    <span className="text-xs font-bold">VISITED</span>
                  </div>
                )}
              </div>

              <div className="pt-4 border-t border-gray-200 space-y-3">
                {/* Address */}
                <div>
                  <label className="font-semibold text-sm text-gray-700 block mb-1">Address</label>
                  <div className="flex items-start gap-2">
                    <div className="flex-1 text-sm text-gray-900">
                      {selectedPoS.street} {selectedPoS.street_number}<br />
                      {selectedPoS.postal_code} {selectedPoS.city}
                    </div>
                    <button 
                      onClick={() => copyAddress(selectedPoS)} 
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Copy address"
                    >
                      <Copy size={16} className="text-gray-600" />
                    </button>
                  </div>
                </div>

                {/* Contact Person */}
                {selectedPoS.contact_person_name && (
                  <div>
                    <label className="font-semibold text-sm text-gray-700 block mb-1">Contact Person</label>
                    <div className="text-sm text-gray-900">{selectedPoS.contact_person_name}</div>
                    {selectedPoS.contact_person_phone && (
                      <a 
                        href={`tel:${selectedPoS.contact_person_phone}`} 
                        className="text-blue-600 flex items-center gap-1 mt-1 hover:underline"
                      >
                        <Phone size={14} /> {selectedPoS.contact_person_phone}
                      </a>
                    )}
                    {selectedPoS.contact_person_email && (
                      <a 
                        href={`mailto:${selectedPoS.contact_person_email}`} 
                        className="text-blue-600 flex items-center gap-1 mt-1 hover:underline"
                      >
                        <Mail size={14} /> {selectedPoS.contact_person_email}
                      </a>
                    )}
                  </div>
                )}

                {/* Follow-up Actions */}
                {selectedPoS.needs_followup && (
                  <>
                    <div className={`p-3 rounded-lg border-2 ${
                      selectedPoS.followup_priority === 'urgent' ? 'bg-fuchsia-50 border-fuchsia-300' :
                      selectedPoS.followup_priority === 'high' ? 'bg-red-50 border-red-200' :
                      selectedPoS.followup_priority === 'medium' ? 'bg-orange-50 border-orange-200' :
                      'bg-blue-50 border-blue-200'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle size={18} className={
                          selectedPoS.followup_priority === 'urgent' ? 'text-fuchsia-700' :
                          selectedPoS.followup_priority === 'high' ? 'text-red-600' :
                          selectedPoS.followup_priority === 'medium' ? 'text-orange-600' :
                          'text-blue-600'
                        } />
                        <label className="font-bold text-sm">Follow-up Required</label>
                        <span className={`ml-auto px-2 py-0.5 rounded-full text-xs font-bold ${
                          selectedPoS.followup_priority === 'urgent' ? 'bg-fuchsia-700 text-white' :
                          selectedPoS.followup_priority === 'high' ? 'bg-red-600 text-white' :
                          selectedPoS.followup_priority === 'medium' ? 'bg-orange-600 text-white' :
                          'bg-blue-600 text-white'
                        }`}>
                          {selectedPoS.followup_priority?.toUpperCase()}
                        </span>
                      </div>
                      
                      {selectedPoS.followup_materials && selectedPoS.followup_materials.length > 0 && (
                        <div className="text-xs mb-2">
                          <strong>Materials:</strong> {selectedPoS.followup_materials.join(', ')}
                        </div>
                      )}
                      
                      {selectedPoS.followup_actions && selectedPoS.followup_actions.length > 0 && (
                        <div className="text-xs">
                          <strong>Actions:</strong> {selectedPoS.followup_actions.join(', ')}
                        </div>
                      )}
                    </div>
                    
                    {/* Follow-up Resolution Buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={async () => {
                          try {
                            // Fetch the full follow-up details
                            const res = await api.get(`/followups/pos/${selectedPoS.pos_id}`);
                            if (res.data) {
                              setQuickVisitFollowup({
                                ...res.data,
                                pos_name: selectedPoS.pos_name,
                                pos_address: `${selectedPoS.street} ${selectedPoS.street_number}, ${selectedPoS.postal_code} ${selectedPoS.city}`,
                                store_type: selectedPoS.store_type,
                                pos_id: selectedPoS.pos_id,
                              });
                              setShowQuickVisitModal(true);
                            } else {
                              toast.warning('No pending follow-up found for this PoS');
                            }
                          } catch (err) {
                            console.error('Failed to fetch follow-up:', err);
                            toast.error('Failed to load follow-up details');
                          }
                        }}
                        className="flex-1 px-4 py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <Zap size={18} />
                        Quick Visit
                      </button>
                      <button
                        onClick={async () => {
                          // Fetch the follow-up visit ID to pass to the revisit wizard
                          try {
                            const res = await api.get(`/followups/pos/${selectedPoS.pos_id}`);
                            if (res.data && res.data.id) {
                              navigate(
                                `/visits/new?posId=${encodeURIComponent(selectedPoS.pos_id)}&resolveFollowupId=${encodeURIComponent(res.data.id)}`
                              );
                            } else {
                              // No pending follow-up, just navigate normally
                              navigate(
                                `/visits/new?posId=${encodeURIComponent(selectedPoS.pos_id)}`
                              );
                            }
                          } catch (err) {
                            // Fallback to normal navigation
                            navigate(
                              `/visits/new?posId=${encodeURIComponent(selectedPoS.pos_id)}`
                            );
                          }
                        }}
                        className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <ChevronRight size={18} />
                        Full Revisit
                      </button>
                    </div>
                  </>
                )}

                {/* Last Visit Info */}
                {selectedPoS.last_visit_date && (
                  <div>
                    <label className="font-semibold text-sm text-gray-700 block mb-2">Last Visit</label>
                    <div
                      onClick={async () => {
                        try {
                          // Fetch the last visit for this PoS
                          const res = await api.get(`/visits?pos_id=${selectedPoS.pos_id}&limit=1`);
                          if (res.data.data && res.data.data.length > 0) {
                            setSelectedVisit(res.data.data[0]);
                            setShowVisitDetailModal(true);
                          } else {
                            toast.warning('No visit details found');
                          }
                        } catch (err) {
                          console.error('Failed to fetch visit:', err);
                          toast.error('Failed to load visit details');
                        }
                      }}
                      className="bg-white border border-gray-100 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <div className="px-4 py-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-semibold text-gray-900">
                              {new Date(selectedPoS.last_visit_date).toLocaleDateString('de-DE', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              })}
                            </p>
                            {selectedPoS.last_visit_type && (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${
                                selectedPoS.last_visit_type === 'first_time' ? 'bg-green-100 text-green-800' :
                                selectedPoS.last_visit_type === 'revisit' ? 'bg-orange-100 text-orange-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {selectedPoS.last_visit_type === 'first_time' ? 'First Visit' :
                                 selectedPoS.last_visit_type === 'revisit' ? 'Revisit' :
                                 'Visit'}
                              </span>
                            )}
                          </div>
                        </div>
                        {selectedPoS.last_visit_outcome && (
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            <span className={`inline-flex items-center px-2 py-1 rounded font-medium ${
                              selectedPoS.last_visit_outcome === 'placement_secured' ? 'bg-green-100 text-green-800' :
                              selectedPoS.last_visit_outcome === 'rejected' ? 'bg-red-100 text-red-800' :
                              selectedPoS.last_visit_outcome === 'follow_up_needed' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {selectedPoS.last_visit_outcome.replace('_', ' ').toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Mark/Unmark as Visited Toggle - Always at bottom */}
              <div className="sticky bottom-0 p-4 bg-white border-t-2 border-gray-200">
                <button 
                  onClick={async () => {
                    try {
                      if (selectedPoS.visited) {
                        // Unmark as visited
                        await api.patch(`/visit-batches/items/${selectedPoS.id}/unvisited`);
                        toast.success('Marked as not visited');
                      } else {
                        // Create a visit for this PoS
                        const visitRes = await api.post('/visits', {
                          pos_id: selectedPoS.pos_id,
                          store_type: selectedPoS.store_type,
                          visit_type: 'revisit',
                          notes: 'Quick visit from route',
                        });

                        // Mark the batch item as visited
                        await api.patch(`/visit-batches/items/${selectedPoS.id}/visited`, {
                          visitId: visitRes.data.id,
                        });
                        
                        toast.success('Als besucht markiert!');
                      }
                      
                      // Refresh route data
                      if (viewMode === 'current') {
                        fetchActiveRoute();
                      } else {
                        fetchArchivedRoutes();
                      }
                    } catch (err) {
                      console.error('Failed to toggle visited status:', err);
                      toast.error('Fehler beim Aktualisieren');
                    }
                  }}
                  className={`w-full px-4 py-3 rounded-xl font-bold transition-colors shadow-lg active:scale-[0.98] ${
                    selectedPoS.visited
                      ? 'bg-gray-600 text-white hover:bg-gray-700'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {selectedPoS.visited ? 'Unmark as Visited ✗' : 'Mark as Visited ✓'}
                </button>
              </div>
            </div>
          ) : (
            <div className="text-gray-400 text-center mt-32">
              <MapPin size={48} className="mx-auto mb-4" />
              <p className="font-medium">Click a marker on the map</p>
              <p className="text-sm">to view PoS details</p>
            </div>
          )}
        </div>
      </div>

      {/* PoS List Below Map */}
      <div className="mt-8">
        <h2 className="text-2xl font-bold mb-4">
          Route Stops ({(viewMode === 'current' ? activeRoute : selectedRoute)?.items?.length || 0})
        </h2>
        <div className="space-y-3">
          {((viewMode === 'current' ? activeRoute : selectedRoute)?.items || []).map((item) => {
            const hasCoordinates = item.latitude && item.longitude;
            
            return (
              <div 
                key={item.id}
                onClick={() => setSelectedPoS(item)}
                className={`p-5 border-2 rounded-xl cursor-pointer transition-all ${
                  selectedPoS?.id === item.id 
                    ? 'bg-purple-50 border-purple-400 shadow-lg' 
                    : hasCoordinates
                    ? 'bg-white border-gray-200 hover:border-gray-400 hover:shadow-md'
                    : 'bg-gray-50 border-gray-300 opacity-60'
                }`}
              >
                <div className="flex items-center gap-4">
                  {/* Number Badge */}
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-black text-lg text-white flex-shrink-0 ${
                    item.visited ? 'bg-green-600' : hasCoordinates ? 'bg-blue-600' : 'bg-gray-400'
                  }`}>
                    {item.sequence_order}
                  </div>
                
                {/* PoS Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="font-black text-gray-900 text-lg">{item.pos_name}</div>
                    {item.visited && (
                      <CheckCircle size={18} className="text-green-600" />
                    )}
                    {!hasCoordinates && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-gray-400 text-white">
                        NO GPS
                      </span>
                    )}
                    {item.needs_followup && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                        item.followup_priority === 'urgent' ? 'bg-fuchsia-700 text-white' :
                        item.followup_priority === 'high' ? 'bg-red-600 text-white' :
                        item.followup_priority === 'medium' ? 'bg-orange-600 text-white' :
                        'bg-blue-600 text-white'
                      }`}>
                        FOLLOW-UP
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-600">{item.street} {item.street_number}, {item.city}</div>
                  {item.contact_person_name && (
                    <div className="text-xs text-gray-500 mt-1">Contact: {item.contact_person_name}</div>
                  )}
                </div>
                
                {/* Distance */}
                <div className="text-right">
                  <div className="text-sm font-bold text-gray-900">{parseFloat(item.distance_from_previous_km as any).toFixed(1)} km</div>
                  <div className="text-xs text-gray-500">Entfernung</div>
                </div>
                
                {/* Copy Button */}
                <button 
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    copyAddress(item); 
                  }}
                  className="p-3 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Copy address"
                >
                  <Copy size={18} className="text-gray-600" />
                </button>
              </div>
            </div>
          );
          })}
        </div>
      </div>

      {/* Visit Detail Modal */}
      <VisitDetailModal
        visit={selectedVisit}
        isOpen={showVisitDetailModal}
        onClose={() => {
          setShowVisitDetailModal(false);
          setSelectedVisit(null);
        }}
      />

      {/* Quick Visit Modal */}
      {quickVisitFollowup && (
        <QuickVisitModal
          isOpen={showQuickVisitModal}
          onClose={() => {
            setShowQuickVisitModal(false);
            setQuickVisitFollowup(null);
          }}
          followup={quickVisitFollowup}
          onComplete={() => {
            setShowQuickVisitModal(false);
            setQuickVisitFollowup(null);
            // Refresh route data
            if (viewMode === 'current') {
              fetchActiveRoute();
            } else {
              fetchArchivedRoutes();
            }
          }}
        />
      )}
    </div>
  );
};

export default Routes;

